/**
 * KNK-1.12 / FR-22, FR-19, FR-21 — Disposition rules and quick buttons.
 *
 * The decisions, with no React, so they can be tested. The data is `pin_status_config` (one row
 * per Disposition, rule columns from migration 20261005a8) and `knock_quick_buttons` (slots 1-5;
 * `team_id` NULL is the Account set, a team's rows replace it for that team's members).
 *
 * ⚠️ THIS IS NOT THE GATE. Every rule below is enforced again by the database, which is the one
 * that counts: the CHECK constraints, `pin_status_config_rules_guard` (codes never change; a used
 * Disposition archives, never deletes; nothing on a quick button archives) and
 * `knock_quick_buttons_guard` (no Do Not Knock, nothing archived, no other account's team). This
 * file exists so the form says what is wrong before the round trip, in the same words.
 *
 * ⚠️ CONFIGURATION ONLY. Creates Lead, Revisit, terminal period and Door fact are stored here and
 * applied by the stories that own them (R2 leads, KNK-1.47 Revisits, KNK-1.24 Door chips).
 */

export const DISPOSITION_CATEGORIES = [
  'No Contact',
  'Not Interested',
  'Not Qualified',
  'Follow Up',
  'Qualifying',
  'Moving Forward',
  'Appointment',
  'Sold',
  'Do Not Knock',
] as const;
export type DispositionCategory = (typeof DISPOSITION_CATEGORIES)[number];

export const DOOR_FACTS = [
  { value: 'occupancy_owner', label: 'Owner' },
  { value: 'occupancy_renter', label: 'Renter' },
  { value: 'has_solar', label: 'Has solar' },
] as const;
export type DoorFact = (typeof DOOR_FACTS)[number]['value'];

/** Slot 6 is always More (§A10), so an Account or team configures five. */
export const QUICK_SLOTS = 5;

export interface Disposition {
  id: string;
  /** Immutable. Reports and history key on it; a rename changes `name` only. */
  code: string;
  name: string;
  category: DispositionCategory;
  color: string;
  sortOrder: number;
  active: boolean;
  requiresNote: boolean;
  createsLead: boolean;
  revisitOffsetHours: number | null;
  terminalDays: number | null;
  doorFact: DoorFact | null;
  doorFactDays: number | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const intOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) ? v : null);
const isCategory = (v: unknown): v is DispositionCategory => DISPOSITION_CATEGORIES.includes(v as DispositionCategory);
const isDoorFact = (v: unknown): v is DoorFact => DOOR_FACTS.some((f) => f.value === v);

/** A row we cannot fully trust is dropped, not half-rendered: the editor would write it back. */
export function parseDisposition(raw: unknown): Disposition | null {
  if (!isRecord(raw)) return null;
  const { id, status_code, display_name, category, color_hex, door_fact } = raw;
  if (typeof id !== 'string' || typeof status_code !== 'string' || typeof display_name !== 'string') return null;
  if (typeof color_hex !== 'string' || !isCategory(category)) return null;
  if (door_fact !== null && door_fact !== undefined && !isDoorFact(door_fact)) return null;
  return {
    id,
    code: status_code,
    name: display_name,
    category,
    color: color_hex,
    sortOrder: intOrNull(raw.sort_order) ?? 0,
    active: raw.is_active !== false,
    requiresNote: raw.requires_notes === true,
    createsLead: raw.creates_lead === true,
    revisitOffsetHours: intOrNull(raw.revisit_offset_hours),
    terminalDays: intOrNull(raw.terminal_days),
    doorFact: isDoorFact(door_fact) ? door_fact : null,
    doorFactDays: intOrNull(raw.door_fact_days),
  };
}

// ---------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------

const CODE_RE = /^[a-z][a-z0-9_]{1,39}$/;

/**
 * A code for a new Disposition, from its name. Matches `pin_status_config_code_format` and never
 * reuses an existing code, archived ones included: a code is history's key, so reusing one would
 * relabel every Knock that ever carried it.
 */
export function codeFromName(name: string, existing: readonly string[]): string {
  let base = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (base === '') base = 'disposition';
  if (!/^[a-z]/.test(base) || base.length < 2) base = `d_${base}`;
  base = base.slice(0, 40).replace(/_+$/, '');

  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `_${n}`;
    const candidate = base.slice(0, 40 - suffix.length).replace(/_+$/, '') + suffix;
    if (!taken.has(candidate) && CODE_RE.test(candidate)) return candidate;
  }
}

// ---------------------------------------------------------------------------
// The edit form
// ---------------------------------------------------------------------------

/** Form state. Numbers are strings because an empty box means "no rule", not zero. */
export interface DispositionDraft {
  name: string;
  category: DispositionCategory;
  color: string;
  requiresNote: boolean;
  createsLead: boolean;
  revisitOffsetHours: string;
  terminalDays: string;
  doorFact: DoorFact | '';
  doorFactDays: string;
}

export const EMPTY_DRAFT: DispositionDraft = {
  name: '',
  category: 'Follow Up',
  color: '#6B7280',
  requiresNote: false,
  createsLead: false,
  revisitOffsetHours: '',
  terminalDays: '',
  doorFact: '',
  doorFactDays: '',
};

export function draftFrom(d?: Disposition): DispositionDraft {
  if (!d) return { ...EMPTY_DRAFT };
  const s = (n: number | null) => (n === null ? '' : String(n));
  return {
    name: d.name,
    category: d.category,
    color: d.color,
    requiresNote: d.requiresNote,
    createsLead: d.createsLead,
    revisitOffsetHours: s(d.revisitOffsetHours),
    terminalDays: s(d.terminalDays),
    doorFact: d.doorFact ?? '',
    doorFactDays: s(d.doorFactDays),
  };
}

/** The columns an edit writes. `status_code` is absent on purpose: it never changes. */
export interface DispositionPatch {
  display_name: string;
  category: DispositionCategory;
  color_hex: string;
  requires_notes: boolean;
  creates_lead: boolean;
  revisit_offset_hours: number | null;
  terminal_days: number | null;
  door_fact: DoorFact | null;
  door_fact_days: number | null;
}

export type DraftField = keyof DispositionDraft;
export type DraftResult = { ok: true; patch: DispositionPatch } | { ok: false; errors: Partial<Record<DraftField, string>> };

function wholeIn(raw: string, min: number, max: number): number | null | 'bad' {
  const t = raw.trim();
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return 'bad';
  const n = Number(t);
  return n >= min && n <= max ? n : 'bad';
}

/** Same bounds as the CHECK constraints in 20261005a8. */
export function validateDraft(draft: DispositionDraft): DraftResult {
  const errors: Partial<Record<DraftField, string>> = {};
  const name = draft.name.trim();
  if (name.length < 1 || name.length > 40) errors.name = 'A name of 1 to 40 characters.';
  if (!/^#[0-9A-Fa-f]{6}$/.test(draft.color)) errors.color = 'A colour like #10B981.';
  if (!isCategory(draft.category)) errors.category = 'Choose a category.';

  const revisit = wholeIn(draft.revisitOffsetHours, 1, 8760);
  if (revisit === 'bad') errors.revisitOffsetHours = 'Whole hours, 1 to 8,760 (a year), or blank for no Revisit.';
  const terminal = wholeIn(draft.terminalDays, 1, 3650);
  if (terminal === 'bad') errors.terminalDays = 'Whole days, 1 to 3,650, or blank if knocking may continue.';
  const factDays = wholeIn(draft.doorFactDays, 1, 3650);
  if (factDays === 'bad') errors.doorFactDays = 'Whole days, 1 to 3,650, or blank for "until changed".';
  else if (factDays !== null && draft.doorFact === '') errors.doorFactDays = 'Choose the Door fact this period applies to.';

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    patch: {
      display_name: name,
      category: draft.category,
      color_hex: draft.color.toUpperCase(),
      requires_notes: draft.requiresNote,
      creates_lead: draft.createsLead,
      revisit_offset_hours: revisit as number | null,
      terminal_days: terminal as number | null,
      door_fact: draft.doorFact === '' ? null : draft.doorFact,
      door_fact_days: factDays as number | null,
    },
  };
}

// ---------------------------------------------------------------------------
// Quick buttons
// ---------------------------------------------------------------------------

export interface QuickButtonRow {
  teamId: number | null;
  slot: number;
  code: string;
}

export function parseQuickButtonRows(raw: unknown): QuickButtonRow[] {
  if (!Array.isArray(raw)) return [];
  const out: QuickButtonRow[] = [];
  for (const r of raw) {
    if (!isRecord(r) || typeof r.status_code !== 'string' || typeof r.slot !== 'number') continue;
    if (r.team_id !== null && typeof r.team_id !== 'number') continue;
    out.push({ teamId: r.team_id, slot: r.slot, code: r.status_code });
  }
  return out.sort((a, b) => a.slot - b.slot);
}

/** The codes a scope has, in slot order; `null` for a team with no set of its own. */
export function quickButtonSetFor(rows: readonly QuickButtonRow[], teamId: number | null): string[] | null {
  const mine = rows.filter((r) => r.teamId === teamId).map((r) => r.code);
  return teamId !== null && mine.length === 0 ? null : mine;
}

/** Account slot per code, for showing "Quick 2" beside a Disposition. */
export function quickSlotsByCode(rows: readonly QuickButtonRow[]): Map<string, number> {
  return new Map(rows.filter((r) => r.teamId === null).map((r) => [r.code, r.slot]));
}

/** Returns a sentence when the set would be refused, or null. Same rules as the database. */
export function validateQuickButtons(codes: readonly string[], dispositions: readonly Disposition[], scope: 'account' | 'team'): string | null {
  if (codes.length === 0) return scope === 'account' ? 'The account needs at least one quick button.' : null;
  if (codes.length > QUICK_SLOTS) return 'At most five quick buttons; the sixth is always More.';
  if (new Set(codes).size !== codes.length) return 'Each quick button must be a different Disposition.';
  for (const code of codes) {
    const d = dispositions.find((x) => x.code === code);
    if (!d) return `"${code}" is not one of this account's Dispositions.`;
    if (!d.active) return `"${d.name}" is archived, so it cannot be a quick button.`;
    if (d.category === 'Do Not Knock') return `"${d.name}" is Do Not Knock. It is always under More, never a quick button.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

export function formatHours(h: number): string {
  if (h % 24 === 0) {
    const days = h / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${h} hour${h === 1 ? '' : 's'}`;
}

/** What a Disposition does beyond recording itself, one phrase per rule. */
export function ruleSummary(d: Disposition): string[] {
  const out: string[] = [];
  if (d.requiresNote) out.push('Note required');
  if (d.createsLead) out.push('Creates a Lead');
  if (d.revisitOffsetHours !== null) out.push(`Revisit in ${formatHours(d.revisitOffsetHours)}`);
  if (d.terminalDays !== null) out.push(`Not knocked again for ${d.terminalDays} days`);
  if (d.doorFact !== null) {
    const label = DOOR_FACTS.find((f) => f.value === d.doorFact)!.label;
    out.push(d.doorFactDays === null ? `Sets ${label}` : `Sets ${label} for ${d.doorFactDays} days`);
  }
  return out;
}

export const DISPOSITIONS_COPY = {
  title: 'Dispositions',
  description: 'What a rep can record at a Door, and what each one does. Codes never change, so reports keep lining up when you rename.',
  add: 'Add Disposition',
  edit: 'Edit',
  archive: 'Archive',
  restore: 'Restore',
  delete: 'Delete',
  archived: 'Archived',
  showArchived: 'Show archived',
  deleteConfirm: 'Delete this Disposition? Only one that has never been used can be deleted; anything else is archived instead.',
  fields: {
    name: 'Name',
    category: 'Category',
    color: 'Colour',
    requiresNote: 'Requires a note',
    requiresNoteHint: 'The Knock saves on the tap and the rep owes a note (FR-21).',
    createsLead: 'Creates a Lead',
    createsLeadHint: 'Applied once Leads arrive (R2).',
    revisitOffsetHours: 'Revisit after (hours)',
    revisitOffsetHint: 'Blank: no Revisit. 48 is two days.',
    terminalDays: 'Do not knock again for (days)',
    terminalHint: 'Blank: the Door stays in play.',
    doorFact: 'Sets Door fact',
    doorFactNone: 'Nothing',
    doorFactDays: 'For (days)',
    doorFactDaysHint: 'Blank: until changed. Renter is 180 by default, then Unknown.',
  },
  dnkNote: 'Do Not Knock Dispositions are guarded: always under More, never a quick button.',
} as const;

export const QUICK_BUTTONS_COPY = {
  title: 'Quick buttons',
  description: 'The five buttons on the Door sheet, in order. The sixth is always More, where every other Disposition lives, Do Not Knock included.',
  scopeAccount: 'Whole account',
  usesAccount: 'This team uses the account’s buttons.',
  customise: 'Give this team its own buttons',
  revert: 'Use the account’s buttons',
  slot: (n: number) => `Button ${n}`,
  empty: 'Empty',
  more: 'More',
  save: 'Save quick buttons',
  saved: 'Quick buttons saved',
} as const;
